import { Card, InlineStack, BlockStack, Text, Button, Modal, Banner, Checkbox, Divider, Combobox, Icon, Listbox, AutoSelection, TextField, Bleed, Link, Tag } from '@shopify/polaris';
import React, { useCallback, useMemo, useState } from 'react';
import { DeleteIcon, DeleteIconCritical } from '../../../Asests/svgIcon/icon';
import { DI, DIProps } from '../../../Core';
import { DeleteMajor, SearchMinor } from '@shopify/polaris-icons';
import { appBridgeRedirect } from '../../Panel/Panel/index'
import { useTranslation } from 'react-i18next';

const Uninstallation = (props: DIProps) => {
    const { t } = useTranslation();
    const [uninstallLoader, setUninstallLoader] = useState(false);
    const [activateFirstModal, setFirstModal] = useState(false);
    const handleFirstModal = useCallback(() => setFirstModal(!activateFirstModal), [activateFirstModal]);
    const [activateSecondModal, setSecondModal] = useState(false);
    const handleSecondModal = useCallback(() => setSecondModal(!activateSecondModal), [activateSecondModal]);
    const [checked, setChecked] = useState(false);
    const handleCheckBoxChange = useCallback(
        (newChecked: boolean) => setChecked(newChecked),
        [],
    );

    const toggleModal = useCallback(() => setFirstModal(!activateFirstModal), [activateFirstModal]);
    const toggleSecondModal = useCallback(() => setSecondModal(!activateSecondModal), [activateSecondModal]);

    const deselectedOptions = useMemo(
        () => [
            { value: 'Not using app now', label: t('Not using app now') },
            { value: 'Hard to setup or use', label: t('Hard to setup or use') },
            { value: 'Not satisfied with support', label: t('Not satisfied with support') },
            { value: 'Limited or missing feature', label: t('Limited or missing feature') },
            { value: 'Expensive or unexpected cost', label: t('Expensive or unexpected cost') },
            { value: 'App security or privacy issues', label: t('App security or privacy issues') },
            { value: 'Not working or compatible with store', label: t('Not working or compatible with store') },
        ],
        [],
    );

    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState(deselectedOptions);

    const updateText = useCallback(
        (value: string) => {
            setInputValue(value);

            if (value === '') {
                setOptions(deselectedOptions);
                return;
            }

            const filterRegex = new RegExp(value, 'i');
            const resultOptions = deselectedOptions.filter((option) =>
                option.label.match(filterRegex),
            );
            setOptions(resultOptions);
        },
        [deselectedOptions],
    );

    const updateSelection = useCallback(
        (selected: string) => {
            if (selectedOptions.includes(selected)) {
                setSelectedOptions(
                    selectedOptions.filter((option) => option !== selected),
                );
            } else {
                setSelectedOptions([...selectedOptions, selected]);
            }

            updateText('');
        },
        [selectedOptions, updateText],
    );


    const optionsMarkup =
        options.length > 0
            ? options.map((option) => {
                const { label, value } = option;

                return (
                    <Listbox.Option
                        key={`${value}`}
                        value={value}
                        selected={selectedOptions.includes(value)}
                        accessibilityLabel={label}
                    >
                        {label}
                    </Listbox.Option>
                );
            })
            : null;

    const [textFieldValue, setTextFieldValue] = useState('');

    const handleTextFieldChange = useCallback(
        (value: string) => setTextFieldValue(value),
        [],
    );

    const removeTag = useCallback(
        (tag: string) => () => {
            const options = [...selectedOptions];
            options.splice(options.indexOf(tag), 1);
            setSelectedOptions(options);
        },
        [selectedOptions],
    );

    const tagsMarkup = selectedOptions.map((option) => (
        <Tag key={`option-${option}`} onRemove={removeTag(option)}>
            {option}
        </Tag>
    ));

    const uninstallApp = () => {
        setUninstallLoader(true);
        props.di.POST('shopifyhome/request/appUninstall', {
            reason: [...selectedOptions],
            feedback: textFieldValue
        }).then((res: any) => {
            if (res?.success) {
                //redirect to shopify admin page
                props.success(t(res?.message) ?? t('Voila! your shopify app has been deleted successfully.'))
                let shop: any = sessionStorage.getItem('shop');
                let username: any = shop.slice(0, shop.indexOf(".myshopify"));
                const tempUrl = "https://admin.shopify.com/store/" + username;
                if (process.env.REACT_APP_PROD === '1') {
                    appBridgeRedirect(tempUrl, props);
                } else {
                    window.open(tempUrl);
                }
            }
            else {
                props.error(t(res?.message) ?? t('Oops! something went wrong.'))
                toggleSecondModal();
            }
            setUninstallLoader(false);
        });
    }

    return (
        <Card roundedAbove='sm'>
            <InlineStack gap="100" align='start' wrap={false}>
                <InlineStack gap="200" align="start" wrap={false}>
                    <span>
                        {/* <DeleteIcon /> */}
                        <Icon source={DeleteMajor} tone='critical' />
                    </span>
                    <BlockStack gap="200">
                        <Text variant="headingMd" as="h6">
                            {t("Uninstall Channel")}
                        </Text>
                        <Text as="span">
                            {t("This will remove CedCommerce Amazon Channel app from your Shopify store.")}
                        </Text>
                        <Text as="span" tone='subdued'>
                            {t("Kindly be aware that uninstalling the app will lead to the permanent loss of all existing data, encompassing imported products, templates, configurations, orders, and more. Unfortunately, this data cannot be restored when reinstalling the app in the future. Consequently, you will need to re-import all your products and commence from a clean slate. If you want to learn more about this or have specific queries, please do not hesitate to contact us.")}
                        </Text>
                    </BlockStack>
                </InlineStack>
                <div style={{ color: '#b1f071' }}>
                    <Button onClick={handleFirstModal} tone='critical'>
                        {t("Uninstall")}
                    </Button>
                </div>
            </InlineStack>
            <Modal
                open={activateFirstModal}
                onClose={handleFirstModal}
                title={t("Uninstall CedCommerce Amazon Channel app?")}
                primaryAction={{
                    content: t('Continue'),
                    onAction: () => {
                        handleFirstModal();
                        handleSecondModal();
                    },
                }}
            >
                <Modal.Section>
                    <BlockStack align='center' inlineAlign='center' gap="500">
                        <BlockStack align='center' inlineAlign="center" gap="400">
                            <DeleteIconCritical />
                            <Text variant="headingSm" as="h6">
                                {t("We are about to delete this app!")}
                            </Text>
                        </BlockStack>
                        <BlockStack gap="200" align='center' inlineAlign='center'>
                            <Text as="span" alignment="center">
                                {t("Are sure you want to uninstall the CedCommerce Amazon Channel app from your Shopify store? In case of any queries or challenges, please contact us!")}
                            </Text>
                            <div>
                                <Link
                                    target='_blank'
                                    url={`https://admin.shopify.com/apps${process.env.REACT_APP_SHOPIFY_APP_BASE}/panel/user/support`}
                                    >
                                    {t("Get Support")}
                                </Link>{' '}
                                {t("or")}{' '}
                                <Link
                                    target='_blank'
                                    url="https://calendly.com/cedcommerce-awe-sales/sales">
                                    {t("Book a Call")}
                                </Link>
                            </div>
                        </BlockStack>
                    </BlockStack>
                </Modal.Section>
            </Modal>
            <Modal
                open={activateSecondModal}
                onClose={() => {
                    handleSecondModal();
                    setChecked(false);
                    setSelectedOptions([]);
                    setTextFieldValue('');
                }}
                title={t("Uninstall Amazon by Cedcommerce?")}
                primaryAction={{
                    content: t('Uninstall'),
                    onAction: uninstallApp,
                    destructive: true,
                    disabled: selectedOptions.length == 0,
                    loading: uninstallLoader
                }}
                secondaryActions={[
                    {
                        content: t('Cancel'),
                        onAction: () => {
                            handleSecondModal();
                            setChecked(false);
                            setSelectedOptions([]);
                            setTextFieldValue('');
                        },
                        disabled: uninstallLoader
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="500">
                        <Banner
                            title={t("Prior to deletion, please ensure that all orders from Amazon via the sales channel have been fulfilled and any returns or refunds have been processed.")}
                            tone="warning"
                        >
                            <Text as="p">{t("Deleting the CedCommerce Amazon Channel app will result in the inability to publish products, fulfill orders, or track performance from the app.")}</Text>
                        </Banner>
                        <Checkbox
                            label={t("I understand the risk of uninstalling CedCommerce Amazon channel.")}
                            checked={checked}
                            onChange={handleCheckBoxChange}
                        />
                        <Divider />
                        <Text variant="headingSm" as="h6">
                            {t("Feedback")}
                        </Text>
                        {
                            <div className={!checked ? "card_dull" : undefined}>
                                <BlockStack gap="400">
                                    <BlockStack gap="200">
                                        <Combobox
                                            allowMultiple
                                            activator={
                                                <Combobox.TextField
                                                    disabled={!checked}
                                                    prefix={<Icon source={SearchMinor} />}
                                                    onChange={updateText}
                                                    label={t("Reason for uninstalling*")}
                                                    value={inputValue}
                                                    placeholder={t("Select all that apply")}
                                                    autoComplete="off"
                                                />
                                            }
                                        >
                                            {optionsMarkup ? (
                                                <Listbox
                                                    autoSelection={AutoSelection.None}
                                                    onSelect={updateSelection}
                                                >
                                                    {optionsMarkup}
                                                </Listbox>
                                            ) : null}
                                        </Combobox>
                                        <InlineStack gap='100'>{tagsMarkup}</InlineStack>
                                    </BlockStack>
                                    <div className='Custom__TextField—Count'>
                                        <TextField
                                            disabled={!checked}
                                            label={t("Share any feedback privately with the app developer (Optional)")}
                                            value={textFieldValue}
                                            onChange={handleTextFieldChange}
                                            maxLength={250}
                                            autoComplete="off"
                                            showCharacterCount
                                            placeholder={t('Fixing a keyboard that types the wrong characters in Windows 10 isn’t too difficult. There are a couple of reasons this could happen too.')}
                                        />
                                    </div>
                                </BlockStack>
                            </div>}
                        <Bleed marginInline="500">
                            <Divider />
                        </Bleed>
                        <div>
                            <Text as="span" tone='subdued'>{t("A request will be sent to the app developer within 48 hours to delete all personal customer information.")} {t("Learn about")} </Text>{' '}
                            <Link target='_blank' url='https://amazon-sales-channel-app-backend.cifapps.com/Data_Privacy_Rights.pdf'>
                                {t("Data Privacy Rights")}
                            </Link>
                        </div>
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </Card >
    );
}

export default DI(Uninstallation);